import type {
  WorkbookDefinition,
  WorkbookSampling,
  WorkbookSourceColumn,
} from "@erp/shared-types";
import { stableSerialize } from "@erp/shared-utils";
import { hashString } from "./fingerprint";
import type { FieldRef, SheetLineage } from "./lineage";
import type { WorkbookPlan } from "./planTypes";
import { sheetKind } from "./sheetKinds";

/** How a Sampled run reads one Source. */
export type SampleSourcePlan =
  /** The rows carrying a sampled key value in `column`. */
  | { sourceId: string; method: "key"; column: string }
  /**
   * The rows whose `column` holds a value a sampled Source looks up in it:
   * `via`'s `column` values in the sampled rows of each `sourceId`, sampled
   * before it. Every lookup into it starts from a sampled Source.
   */
  | {
      sourceId: string;
      method: "derived";
      column: string;
      via: { sourceId: string; column: string; matchColumn: string }[];
    }
  /** Every row: nothing samples it, or something looks it up by a value no Source column holds unchanged. */
  | { sourceId: string; method: "whole" };

/** A lookup between two Source columns, traced through the Worksheets between them. */
export interface SampleLookup {
  sheetId: string;
  from: WorkbookSourceColumn;
  to: WorkbookSourceColumn;
}

/**
 * A sampling plan: how a Sampled run reads each Source (the key's Sources
 * first, then the Sources narrowed through lookups, in the order they can be
 * sampled, then the rest), the lookups between Source columns (whose match
 * rates the Representativeness report compares), and the columns the
 * Workbook reads of each sampled Source (automatic strata and blank rates).
 */
export interface SamplingPlan {
  sources: SampleSourcePlan[];
  lookups: SampleLookup[];
  columns: WorkbookSourceColumn[];
  /** Why the Sampling can't be used, in the Workbook's words; empty when it can. */
  problems: string[];
}

/** The most columns a sampling plan reads of each sampled Source. */
const MAX_COLUMNS_PER_SOURCE = 30;

function lineages(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
): Map<string, SheetLineage> {
  const result = new Map<string, SheetLineage>();
  for (const sheet of definition.sheets) {
    const sheetPlan = plan.sheets.get(sheet.id);
    if (sheetPlan) result.set(sheet.id, sheetKind(sheet).lineage(sheetPlan));
  }
  return result;
}

/**
 * Traces a column back to the Source column it is unchanged from, through
 * Worksheets' lineage; null when a Worksheet changes it on the way.
 */
function sourceOrigin(
  field: FieldRef,
  sourceIds: ReadonlySet<string>,
  sheets: ReadonlyMap<string, SheetLineage>,
): WorkbookSourceColumn | null {
  let current: FieldRef | undefined = field;
  // Execution order has no cycles among runnable Worksheets; the bound keeps a blocked one from looping.
  for (let depth = 0; current && depth <= sheets.size; depth += 1) {
    if (sourceIds.has(current.tableId))
      return { sourceId: current.tableId, column: current.column };
    current = sheets
      .get(current.tableId)
      ?.fields.get(current.column.toLowerCase());
  }
  return null;
}

/** Every lookup, with the Source columns it looks up and matches when both trace to one. */
function tracedLookups(
  definition: WorkbookDefinition,
  sheets: ReadonlyMap<string, SheetLineage>,
) {
  const sourceIds = new Set(definition.sources.map((source) => source.id));
  return [...sheets].flatMap(([sheetId, lineage]) =>
    lineage.lookups.map((lookup) => ({
      sheetId,
      from: lookup.value && sourceOrigin(lookup.value, sourceIds, sheets),
      to: sourceOrigin(lookup.match, sourceIds, sheets),
    })),
  );
}

function sameColumn(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function keyProblems(
  definition: WorkbookDefinition,
  sampling: WorkbookSampling,
  sourceColumns: ReadonlyMap<string, readonly string[]>,
): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  const check = (entry: WorkbookSourceColumn, what: string) => {
    const source = definition.sources.find(
      (item) => item.id === entry.sourceId,
    );
    if (!source) {
      problems.push(`The sampling ${what} names a source that doesn't exist.`);
      return;
    }
    const columns = sourceColumns.get(source.id);
    if (columns && !columns.some((column) => sameColumn(column, entry.column)))
      problems.push(`'${source.name}' has no column '${entry.column}'.`);
  };
  for (const entry of sampling.keyColumns) {
    check(entry, "key");
    if (seen.has(entry.sourceId))
      problems.push("The sampling key names one column per source.");
    seen.add(entry.sourceId);
  }
  for (const entry of sampling.strata) check(entry, "stratum");
  return problems;
}

/**
 * The sampling plan for `sampling` (see `SamplingPlan`). A Source is narrowed
 * through lookups only when every lookup into it starts from a Source sampled
 * before it, so no lookup misses a row the full data would have matched;
 * otherwise it is read whole.
 */
export function samplingPlan(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
  sampling: WorkbookSampling,
  sourceColumns: ReadonlyMap<string, readonly string[]>,
): SamplingPlan {
  const sheets = lineages(definition, plan);
  const traced = tracedLookups(definition, sheets);
  const sources: SampleSourcePlan[] = [];
  const planned = new Set<string>();
  for (const { sourceId, column } of sampling.keyColumns)
    if (!planned.has(sourceId)) {
      planned.add(sourceId);
      sources.push({ sourceId, method: "key", column });
    }
  // Each pass narrows the Sources whose every lookup starts from one planned already.
  for (let next = derivedSources(definition, traced, planned); next.length;) {
    for (const source of next) planned.add(source.sourceId);
    sources.push(...next);
    next = derivedSources(definition, traced, planned);
  }
  for (const source of definition.sources)
    if (!planned.has(source.id))
      sources.push({ sourceId: source.id, method: "whole" });
  const lookups = uniqueBy(
    traced.flatMap(({ sheetId, from, to }) =>
      from && to ? [{ sheetId, from, to }] : [],
    ),
    (lookup) => stableSerialize(lookup),
  );
  return {
    sources,
    lookups,
    columns: readColumns(definition, sheets, sources, sampling),
    problems: keyProblems(definition, sampling, sourceColumns),
  };
}

type TracedLookup = ReturnType<typeof tracedLookups>[number];

/** The Sources not yet `planned` that can be narrowed now: see {@link derivedSource}. */
function derivedSources(
  definition: WorkbookDefinition,
  traced: readonly TracedLookup[],
  planned: ReadonlySet<string>,
): SampleSourcePlan[] {
  return definition.sources.flatMap((source) => {
    if (planned.has(source.id)) return [];
    const derived = derivedSource(source.id, traced, planned);
    return derived ? [derived] : [];
  });
}

/**
 * A Source narrowed through lookups: every lookup into it starts from a
 * planned Source column unchanged, and all of them match one column of it.
 * Undefined when nothing looks it up, or any lookup doesn't qualify.
 */
function derivedSource(
  sourceId: string,
  traced: readonly TracedLookup[],
  planned: ReadonlySet<string>,
): SampleSourcePlan | undefined {
  const into = traced.filter((lookup) => lookup.to?.sourceId === sourceId);
  const via = into.flatMap(({ from, to }) =>
    from && to && planned.has(from.sourceId)
      ? [{ ...from, matchColumn: to.column }]
      : [],
  );
  const [first] = via;
  if (!first || via.length !== into.length) return undefined;
  if (via.some((item) => !sameColumn(item.matchColumn, first.matchColumn)))
    return undefined;
  return {
    sourceId,
    method: "derived",
    column: first.matchColumn,
    via: uniqueBy(via, (item) =>
      stableSerialize([item.sourceId, item.column.toLowerCase()]),
    ),
  };
}

function uniqueBy<T>(items: readonly T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = key(item);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** The columns the Workbook reads of each sampled Source, its key and strata first. */
function readColumns(
  definition: WorkbookDefinition,
  sheets: ReadonlyMap<string, SheetLineage>,
  sources: readonly SampleSourcePlan[],
  sampling: WorkbookSampling,
): WorkbookSourceColumn[] {
  const sourceIds = new Set(definition.sources.map((source) => source.id));
  const sampled = new Set(
    sources
      .filter((source) => source.method !== "whole")
      .map((source) => source.sourceId),
  );
  const read = [
    ...sampling.keyColumns,
    ...sampling.strata,
    ...[...sheets.values()].flatMap((lineage) =>
      lineage.reads.flatMap((field) => {
        const origin = sourceOrigin(field, sourceIds, sheets);
        return origin ? [origin] : [];
      }),
    ),
  ].filter((column) => sampled.has(column.sourceId));
  const perSource = new Map<string, number>();
  return uniqueBy(read, (column) =>
    stableSerialize([column.sourceId, column.column.toLowerCase()]),
  ).filter((column) => {
    const count = perSource.get(column.sourceId) ?? 0;
    perSource.set(column.sourceId, count + 1);
    return count < MAX_COLUMNS_PER_SOURCE;
  });
}

/** A Sampling key the Workbook's own lookups suggest: one column in each Source it links. */
export interface SamplingKeySuggestion {
  /** The name most of its columns share. */
  name: string;
  keyColumns: WorkbookSourceColumn[];
  /** How many lookups between those Sources it carries. */
  lookupCount: number;
}

/**
 * Sampling keys the Workbook's lookups suggest, best first: Source columns
 * joined by lookups that trace back to them unchanged (an item number looked
 * up from the sales file in the stock file), grouped, each group one
 * candidate key. The more Sources and lookups a key links, the better.
 */
export function suggestSamplingKeys(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
): SamplingKeySuggestion[] {
  const sheets = lineages(definition, plan);
  const id = (column: WorkbookSourceColumn) =>
    stableSerialize([column.sourceId, column.column.toLowerCase()]);
  const parent = new Map<string, string>();
  const columns = new Map<string, WorkbookSourceColumn>();
  const find = (key: string): string => {
    const up = parent.get(key) ?? key;
    if (up === key) return key;
    const root = find(up);
    parent.set(key, root);
    return root;
  };
  const edges = tracedLookups(definition, sheets).flatMap(({ from, to }) =>
    from && to && from.sourceId !== to.sourceId ? [{ from, to }] : [],
  );
  for (const { from, to } of edges) {
    columns.set(id(from), from);
    columns.set(id(to), to);
    parent.set(find(id(from)), find(id(to)));
  }
  const groups = new Map<string, WorkbookSourceColumn[]>();
  for (const [key, column] of columns) {
    const root = find(key);
    groups.set(root, [...(groups.get(root) ?? []), column]);
  }
  return [...groups]
    .map(([root, members]) => {
      const keyColumns = uniqueBy(members, (column) => column.sourceId);
      return {
        name: mostCommon(keyColumns.map((column) => column.column)),
        keyColumns,
        lookupCount: edges.filter((edge) => find(id(edge.from)) === root)
          .length,
      };
    })
    .filter((suggestion) => suggestion.keyColumns.length > 1)
    .sort(
      (a, b) =>
        b.keyColumns.length - a.keyColumns.length ||
        b.lookupCount - a.lookupCount ||
        a.name.localeCompare(b.name),
    );
}

function mostCommon(names: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const name of names) counts.set(name, (counts.get(name) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
}

/**
 * A sample's identity: the Sampling, its plan and each Source's Fingerprint
 * (with the Data version it was read at). Equal keys take the same sample, so
 * a Sampled run whose key matches an earlier one's reuses its sample.
 */
export function samplingKey(
  sampling: WorkbookSampling,
  plan: SamplingPlan,
  sourceFingerprints: readonly (readonly [string, string])[],
): string {
  return hashString(
    stableSerialize([
      sampling,
      plan.sources,
      plan.columns,
      plan.lookups,
      [...sourceFingerprints].sort(([a], [b]) => a.localeCompare(b)),
    ]),
  );
}
